import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  TENANT_WIZARD_STEPS,
  TENANT_WIZARD_STEP_COUNT,
  TENANT_WIZARD_STEP_LABELS,
  clearDraft,
  nextStep,
  prevStep,
  readDraft,
  stepIndex,
  writeDraft,
  type TenantWizardDraft,
  type TenantWizardStep,
} from '../../../lib/tenant-draft.js';

export type WizardRenderProps = {
  step: TenantWizardStep;
  payload: Record<string, unknown>;
  updatePayload: (patch: Record<string, unknown>) => void;
  /** Programmatic navigation to a specific step (e.g. Step 7 "fix" chips). */
  goToStep: (step: TenantWizardStep) => void;
};

type WizardShellProps = {
  /** Optional renderer for the current step body. Step components (D1.5.2+)
   * will be wired in here. D1.5.1 ships a placeholder for each step. */
  renderStep?: (props: WizardRenderProps) => ReactNode;
};

export function defaultRenderStep({ step }: WizardRenderProps): ReactNode {
  return (
    <div
      className="ferro-muted"
      role="note"
      aria-label={`${TENANT_WIZARD_STEP_LABELS[step]} step placeholder`}
    >
      <p>
        {TENANT_WIZARD_STEP_LABELS[step]} step — form fields wire up in later tasks
        (D1.5.2 through D1.5.8). This shell persists your draft and survives reloads.
      </p>
    </div>
  );
}

export function WizardShell({ renderStep = defaultRenderStep }: WizardShellProps): JSX.Element {
  const [draft, setDraft] = useState<TenantWizardDraft>(() => {
    const stored = readDraft();
    if (stored) return stored;
    return writeDraft({ lastStep: TENANT_WIZARD_STEPS[0], payload: {} });
  });

  // Re-persist whenever the draft changes. `writeDraft` stamps `updatedAt`,
  // so we compare on (lastStep, payload) to avoid a write loop on mount.
  useEffect(() => {
    writeDraft({ lastStep: draft.lastStep, payload: draft.payload });
  }, [draft.lastStep, draft.payload]);

  const updatePayload = useCallback((patch: Record<string, unknown>) => {
    setDraft((prev) => ({
      ...prev,
      payload: { ...prev.payload, ...patch },
    }));
  }, []);

  const goToStep = useCallback((step: TenantWizardStep) => {
    setDraft((prev) => ({ ...prev, lastStep: step }));
  }, []);

  const currentIndex = stepIndex(draft.lastStep);
  const atFirst = currentIndex === 0;
  const atLast = currentIndex === TENANT_WIZARD_STEP_COUNT - 1;
  const progressPct = useMemo(
    () => Math.round(((currentIndex + 1) / TENANT_WIZARD_STEP_COUNT) * 100),
    [currentIndex],
  );

  const handleBack = useCallback(() => {
    const prev = prevStep(draft.lastStep);
    if (prev) goToStep(prev);
  }, [draft.lastStep, goToStep]);

  const handleNext = useCallback(() => {
    const next = nextStep(draft.lastStep);
    if (next) goToStep(next);
  }, [draft.lastStep, goToStep]);

  const handleDiscard = useCallback(() => {
    clearDraft();
    setDraft(writeDraft({ lastStep: TENANT_WIZARD_STEPS[0], payload: {} }));
  }, []);

  return (
    <section className="ferro-admin-page" aria-label="Tenant provisioning wizard">
      <header className="ferro-admin-page__header">
        <h1 className="ferro-section__title">New tenant</h1>
        <p className="ferro-muted">
          Step {currentIndex + 1} of {TENANT_WIZARD_STEP_COUNT} —{' '}
          {TENANT_WIZARD_STEP_LABELS[draft.lastStep]}
        </p>
      </header>

      <ol
        className="ferro-wizard__steps"
        aria-label={`Wizard progress: ${progressPct}% complete`}
      >
        {TENANT_WIZARD_STEPS.map((s, i) => {
          const isActive = s === draft.lastStep;
          const isComplete = i < currentIndex;
          return (
            <li
              key={s}
              className={
                'ferro-wizard__step'
                + (isActive ? ' ferro-wizard__step--active' : '')
                + (isComplete ? ' ferro-wizard__step--complete' : '')
              }
              aria-current={isActive ? 'step' : undefined}
            >
              <button
                type="button"
                className="ferro-wizard__stepButton"
                onClick={() => goToStep(s)}
                data-step={s}
              >
                <span className="ferro-wizard__stepIndex">{i + 1}</span>
                <span className="ferro-wizard__stepLabel">
                  {TENANT_WIZARD_STEP_LABELS[s]}
                </span>
              </button>
            </li>
          );
        })}
      </ol>

      <div className="ferro-wizard__body">
        {renderStep({
          step: draft.lastStep,
          payload: draft.payload,
          updatePayload,
          goToStep,
        })}
      </div>

      <footer className="ferro-wizard__footer">
        <div className="ferro-wizard__nav">
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={handleBack}
            disabled={atFirst}
          >
            Back
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            onClick={handleNext}
            disabled={atLast}
          >
            Save & continue
          </button>
        </div>
        <div className="ferro-wizard__meta">
          <span className="ferro-muted" aria-live="polite">
            Draft saved {new Date(draft.updatedAt).toLocaleTimeString()}
          </span>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={handleDiscard}
          >
            Discard draft
          </button>
        </div>
      </footer>
    </section>
  );
}
